<script setup lang="ts">
import LandingNewsletterForm from '@/components/layout/LandingNewsletterForm.vue'
import { landingUrl } from '@/utils/landing'
import logoLight from '@images/swida/logo-light.svg'
import facebookIcon from '@images/swida/facebook.svg'
import instagramIcon from '@images/swida/instagram.svg'
import linkedinIcon from '@images/swida/linkedin.svg'

const currentYear = new Date().getFullYear()

const pageLinks = [
  { label: 'Security info', path: '/security-info', primary: false },
  { label: 'GDPR', path: '/processing-of-personal-data', primary: false },
  { label: 'Get your quote', path: '/#quote', primary: true },
]

const socialLinks = [
  { name: 'Facebook', url: 'https://www.facebook.com/swidainnovative', icon: facebookIcon },
  { name: 'Instagram', url: 'https://www.instagram.com/swida.innovative/', icon: instagramIcon },
  { name: 'LinkedIn', url: 'https://www.linkedin.com/company/swida-innovative/', icon: linkedinIcon },
]

const legalLinks = [
  { label: 'Terms', path: '/general-business-terms-and-conditions' },
  { label: 'Cookies', path: '/cookies' },
]
</script>

<template>
  <footer id="contact" class="sw-footer">
    <div class="sw-footer-main">
      <div class="sw-container-box">
        <div class="sw-footer-top">
          <div class="sw-footer-title-wrap">
            <h6 class="sw-footer-title">
              {{ $t('Stay connected with SWIDA') }}
            </h6>
          </div>
          <div class="sw-footer-logo-wrap">
            <a :href="landingUrl('/')" aria-label="Swida">
              <img
                :src="logoLight" alt="Swida Logo"
                width="168"
                height="41" class="sw-footer-logo"
              >
            </a>
          </div>
        </div>

        <div class="sw-footer-grid">
          <div class="sw-footer-area">
            <div class="contact-block">
              <div>
                <span class="text-h3">{{ $t('Sales Department') }}</span>
              </div>
              <div class="contact-block-rows">
                <div class="contact-item">
                  <span class="contact-item-icon">
                    <svg
                      width="16" height="16"
                      viewBox="0 0 16 16"
                      fill="none" xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M2.79961 3.2002C2.13711 3.2002 1.59961 3.7377 1.59961 4.4002C1.59961 4.7777 1.77711 5.1327 2.07961 5.3602L7.27961 9.2602C7.70711 9.5802 8.29211 9.5802 8.71961 9.2602L13.9196 5.3602C14.2221 5.1327 14.3996 4.7777 14.3996 4.4002C14.3996 3.7377 13.8621 3.2002 13.1996 3.2002H2.79961ZM1.59961 6.5002V11.2002C1.59961 12.0827 2.31711 12.8002 3.19961 12.8002H12.7996C13.6821 12.8002 14.3996 12.0827 14.3996 11.2002V6.5002L9.43961 10.2202C8.58711 10.8602 7.41211 10.8602 6.55961 10.2202L1.59961 6.5002Z" fill="white" />
                    </svg>
                  </span>
                  <a href="mailto:transports@swida.sk" target="_blank" rel="noopener noreferrer">transports@swida.sk</a>
                </div>
                <div class="contact-item">
                  <span class="contact-item-icon">
                    <svg
                      width="16" height="16"
                      viewBox="0 0 16 16"
                      fill="none" xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M5.60464 2.22491C5.40714 1.75241 4.89214 1.50241 4.40214 1.63491L4.26464 1.67241C2.64964 2.11241 1.26964 3.67741 1.67214 5.58241C2.59964 9.95741 6.04214 13.3999 10.4171 14.3274C12.3246 14.7324 13.8871 13.3499 14.3271 11.7349L14.3646 11.5974C14.4996 11.1049 14.2471 10.5899 13.7771 10.3949L11.3446 9.38241C10.9321 9.20991 10.4546 9.32991 10.1696 9.67741L9.20464 10.8574C7.44714 9.98491 6.03214 8.52491 5.21964 6.73241L6.32464 5.83241C6.67214 5.54991 6.78964 5.07241 6.61964 4.65741L5.60464 2.22491Z" fill="white" />
                    </svg>
                  </span>
                  <a href="tel:+421552289123" target="_blank" rel="noopener noreferrer">+421 55 22 89 123</a>
                </div>
              </div>
            </div>

            <div class="contact-block">
              <div>
                <span class="text-h3">{{ $t('Dispatching Department') }}</span>
              </div>
              <div class="contact-block-columns">
                <div>
                  <div class="contact-item">
                    <span class="contact-item-icon">
                      <svg
                        width="16" height="16"
                        viewBox="0 0 16 16"
                        fill="none" xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M2.79961 3.2002C2.13711 3.2002 1.59961 3.7377 1.59961 4.4002C1.59961 4.7777 1.77711 5.1327 2.07961 5.3602L7.27961 9.2602C7.70711 9.5802 8.29211 9.5802 8.71961 9.2602L13.9196 5.3602C14.2221 5.1327 14.3996 4.7777 14.3996 4.4002C14.3996 3.7377 13.8621 3.2002 13.1996 3.2002H2.79961ZM1.59961 6.5002V11.2002C1.59961 12.0827 2.31711 12.8002 3.19961 12.8002H12.7996C13.6821 12.8002 14.3996 12.0827 14.3996 11.2002V6.5002L9.43961 10.2202C8.58711 10.8602 7.41211 10.8602 6.55961 10.2202L1.59961 6.5002Z" fill="white" />
                      </svg>
                    </span>
                    <a href="mailto:dispatching@swida.sk" target="_blank" rel="noopener noreferrer">dispatching@swida.sk</a>
                  </div>
                  <div class="contact-item-note">
                    {{ $t('MON-FRI 8:00-17:00') }}
                  </div>
                </div>
                <div>
                  <div class="contact-item">
                    <span class="contact-item-icon">
                      <svg
                        width="16" height="16"
                        viewBox="0 0 16 16"
                        fill="none" xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M2.79961 3.2002C2.13711 3.2002 1.59961 3.7377 1.59961 4.4002C1.59961 4.7777 1.77711 5.1327 2.07961 5.3602L7.27961 9.2602C7.70711 9.5802 8.29211 9.5802 8.71961 9.2602L13.9196 5.3602C14.2221 5.1327 14.3996 4.7777 14.3996 4.4002C14.3996 3.7377 13.8621 3.2002 13.1996 3.2002H2.79961ZM1.59961 6.5002V11.2002C1.59961 12.0827 2.31711 12.8002 3.19961 12.8002H12.7996C13.6821 12.8002 14.3996 12.0827 14.3996 11.2002V6.5002L9.43961 10.2202C8.58711 10.8602 7.41211 10.8602 6.55961 10.2202L1.59961 6.5002Z" fill="white" />
                      </svg>
                    </span>
                    <a href="mailto:nightshift@swida.sk" target="_blank" rel="noopener noreferrer">nightshift@swida.sk</a>
                  </div>
                  <div class="contact-item-note">
                    {{ $t('MON-FRI 17:00-8:00 and') }}<br>{{ $t('SAT-SUN 00:00-23:59') }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="sw-footer-area">
            <div class="newsletter-box">
              <div class="newsletter-box-inner">
                <div class="newsletter-box-head">
                  <div class="newsletter-box-title-row">
                    <span class="text-h3 newsletter-box-title">{{ $t('Subscribe to our newsletter') }}</span>
                    <svg
                      class="newsletter-box-icon" width="20"
                      height="20"
                      viewBox="0 0 20 20" fill="none"
                      xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
                    >
                      <path d="M17.7627 1.17773C18.0689 1.06836 18.4064 1.14648 18.6377 1.37461C18.8689 1.60273 18.9439 1.94336 18.8346 2.24961L13.2721 17.7777C13.1158 18.2121 12.7064 18.4996 12.2471 18.4996C11.8033 18.4996 11.4033 18.2309 11.2377 17.8215L9.23145 12.884C9.09082 12.5402 9.15332 12.1465 9.39395 11.8652L12.3471 8.35273C12.5064 8.16211 12.4939 7.88398 12.3189 7.70898C12.1439 7.53398 11.8627 7.52148 11.6752 7.68086L8.1627 10.6277C7.87832 10.8652 7.4877 10.9277 7.14395 10.7902L2.19082 8.77461C1.78145 8.60898 1.5127 8.20898 1.5127 7.76523C1.5127 7.30586 1.8002 6.89648 2.23457 6.74023L17.7627 1.17773Z" fill="#D81B5D" />
                    </svg>
                  </div>
                  <div class="newsletter-box-description">
                    {{ $t('Stay on top of the latest transport tips, news, trends and best practises.') }}
                  </div>
                </div>
                <LandingNewsletterForm />
              </div>
            </div>
          </div>

          <div class="sw-footer-area">
            <ul class="page-links">
              <li v-for="link in pageLinks" :key="link.path">
                <a
                  :href="landingUrl(link.path)"
                  :aria-label="$t(link.label)"
                  class="page-link-button"
                  :class="link.primary ? 'page-link-button--primary' : 'page-link-button--white'"
                >{{ $t(link.label) }}</a>
              </li>
            </ul>
          </div>

          <div class="sw-footer-area">
            <div class="social-icons">
              <div class="social-icons-inner">
                <h6 class="text-h3 social-icons-title">
                  {{ $t('Follow Us') }}
                </h6>
                <div class="social-icons-list">
                  <a
                    v-for="item in socialLinks"
                    :key="item.name"
                    :href="item.url"
                    :aria-label="item.name"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="social-icon-link"
                  >
                    <img :src="item.icon" alt="" class="social-icon">
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sw-footer-bottom">
      <div class="sw-container-box">
        <div class="sw-footer-bottom-row">
          <div class="sw-footer-bottom-left">
            <span>© {{ currentYear }}</span>
            <span>{{ $t('Swida Innovative s.r.o. — Košice, Slovakia') }}</span>
          </div>
          <ul class="legal-links">
            <li v-for="link in legalLinks" :key="link.path">
              <a :href="landingUrl(link.path)" :aria-label="$t(link.label)">{{ $t(link.label) }}</a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.sw-footer {
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.sw-footer *,
.sw-footer *::before,
.sw-footer *::after {
  box-sizing: border-box;
}

.sw-footer a {
  color: inherit;
  text-decoration: none;
}

.sw-footer ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.sw-footer img,
.sw-footer svg {
  display: block;
  vertical-align: middle;
}

.sw-container-box {
  position: relative;
  width: 100%;
  max-width: min(1120px, 90%);
  margin: 0 auto;
}

.sw-footer-main {
  padding: 48px 0;
  background-color: #000000;
}

.sw-footer-top {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 40px;
  color: #FFFFFF;
}

.sw-footer-title-wrap {
  flex: 1 1 0%;
  order: 2;
  min-width: 0;
}

.sw-footer-title {
  margin: 0 0 12px;
  color: #FFFFFF;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 2.125rem;
}

.sw-footer-logo-wrap {
  flex-shrink: 0;
  order: 1;
}

.sw-footer-logo {
  width: 140px;
  height: auto;
}

.sw-footer-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 40px;
}

.sw-footer-area {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.text-h3 {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.625rem;
}

.contact-block {
  display: flex;
  flex-direction: column;
  gap: 40px;
  color: #FFFFFF;
}

.contact-block-rows {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.contact-block-columns {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 24px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #FFFFFF;
}

.contact-item-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  overflow: hidden;
  border-radius: 9999px;
}

.contact-item a:hover {
  color: #FFFFFF;
}

.contact-item-note {
  margin-top: 8px;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 500;
}

.newsletter-box {
  position: relative;
  overflow: hidden;
  padding: 24px;
  border-radius: 24px;
  background-color: #0E0F10;
}

.newsletter-box-inner {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.newsletter-box-head {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.newsletter-box-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.newsletter-box-title {
  display: block;
  color: #FFFFFF;
}

.newsletter-box-icon {
  flex-shrink: 0;
}

.newsletter-box-description {
  color: #FFFFFF;
  font-size: 14px;
  line-height: 20px;
}

.page-links {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 8px;
  margin-top: 16px !important;
  font-size: 14px;
  line-height: 20px;
}

.page-links li {
  flex-shrink: 0;
}

.page-link-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.page-link-button--white {
  background-color: #FFFFFF;
  color: #000000 !important;
}

.page-link-button--white:hover {
  color: #D81B5D !important;
}

.page-link-button--primary {
  background-color: #D81B5D;
  color: #FFFFFF !important;
}

.page-link-button--primary:hover {
  background-color: #AE134A;
}

.social-icons {
  display: flex;
  justify-content: flex-start;
}

.social-icons-inner {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
}

.social-icons-title {
  margin: 0;
  color: #FFFFFF;
}

.social-icons-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.social-icon-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #292B2D;
}

.social-icon {
  width: 20px;
  height: 20px;
}

.sw-footer-bottom {
  padding: 24px 0;
  background-color: #1C1F22;
  color: #BFC2D4;
}

.sw-footer-bottom-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.sw-footer-bottom-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  line-height: 20px;
}

.legal-links {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 8px;
  margin-top: 16px !important;
  font-size: 14px;
  line-height: 20px;
}

.legal-links a:hover {
  color: #BFC2D4;
}

@media (min-width: 640px) {
  .sw-footer-main {
    padding: 80px 0;
  }

  .sw-footer-top {
    flex-direction: row;
    justify-content: space-between;
    gap: 32px;
    margin-bottom: 64px;
  }

  .sw-footer-title-wrap {
    order: 1;
  }

  .sw-footer-title {
    margin-bottom: 16px;
    font-size: 2.125rem;
    line-height: 2.5rem;
  }

  .sw-footer-logo-wrap {
    order: 2;
  }

  .sw-footer-logo {
    width: 168px;
  }

  .text-h3 {
    font-size: 1.3125rem;
    line-height: 1.75rem;
  }

  .contact-block-columns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    justify-content: space-between;
  }

  .newsletter-box {
    padding: 32px;
  }

  .newsletter-box-inner {
    gap: 32px;
  }

  .page-links {
    gap: 12px;
    margin-top: 0 !important;
  }

  .page-link-button {
    padding: 16px 20px;
    font-size: 16px;
    line-height: 24px;
  }

  .social-icons-list {
    gap: 20px;
  }

  .social-icon-link {
    width: 60px;
    height: 60px;
  }

  .social-icon {
    width: 24px;
    height: 24px;
  }

  .sw-footer-bottom {
    padding: 32px 0;
  }

  .sw-footer-bottom-row {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }

  .sw-footer-bottom-left {
    justify-content: flex-start;
  }

  .legal-links {
    gap: 12px;
    margin-top: 0 !important;
  }
}

@media (min-width: 768px) {
  .sw-footer-main {
    padding: 128px 0;
  }

  .sw-footer-top {
    margin-bottom: 80px;
  }

  .sw-footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 48px;
  }

  .newsletter-box {
    padding: 40px;
  }

  .newsletter-box-inner {
    gap: 40px;
  }

  .newsletter-box-description {
    font-size: 16px;
    line-height: 24px;
  }

  .page-links,
  .legal-links {
    gap: 16px;
  }

  .social-icons {
    justify-content: flex-end;
  }

  .social-icons-inner {
    width: auto;
  }
}

@media (min-width: 1024px) {
  .sw-footer-main {
    padding: 160px 0;
  }

  .sw-footer-title {
    font-size: 2.375rem;
    line-height: 2.75rem;
  }

  .sw-footer-grid {
    gap: 56px;
  }

  .text-h3 {
    font-size: 1.375rem;
    line-height: 1.875rem;
  }
}

@media (min-width: 1280px) {
  .sw-footer-title {
    font-size: 2.625rem;
    line-height: 3rem;
  }
}
</style>
